import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, ShieldAlert, Lock, MessageCircle, ChevronDown } from "lucide-react";

const WHATSAPP_LINK = "https://wa.me/41796780069";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function LandingPage() {
  const [showFab, setShowFab] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowFab(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-body relative overflow-x-hidden">
      {/* Background blobs */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-gradient-to-tr from-[#7ACFFF]/25 to-[#62D7D1]/25 blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 rounded-full bg-gradient-to-br from-[#62D7D1]/15 to-[#7ACFFF]/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-gradient-to-tl from-[#25D366]/10 to-[#62D7D1]/10 blur-3xl" />
      </div>

      {/* Header */}
      <header
        data-testid="landing-header"
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-100"
      >
        <div className="flex justify-center items-center py-4 px-6">
          <img src="/bmg_logo.png" alt="BMG" className="h-8 w-auto" />
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 flex flex-col items-center text-center pt-28 sm:pt-32 pb-10 px-4">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
          className="mb-6 relative"
        >
          {/* Anna avatar */}
          <div className="relative inline-block">
            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white shadow-[0_8px_40px_rgba(98,215,209,0.18)]">
              <img
                src="/anna_avatar.jpg"
                alt="Anna"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Online indicator */}
            <span className="absolute bottom-1 right-1 w-5 h-5 bg-[#25D366] border-[3px] border-white rounded-full" />
          </div>
        </motion.div>

        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
          className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4 max-w-md"
        >
          Work safely.
        </motion.h1>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
          className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-sm sm:max-w-md mb-8 px-2"
        >
          Hi, I'm <span className="font-semibold text-slate-800">Anna</span>, your BMG assistant.
          I'm here to keep you informed instantly on WhatsApp.
        </motion.p>

        <motion.a
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-cta-hero"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1DA851] text-white text-lg font-semibold py-4 px-8 rounded-full shadow-[0_0_20px_rgba(37,211,102,0.4)] hover:shadow-[0_0_30px_rgba(37,211,102,0.55)] transition-all duration-300 transform hover:-translate-y-1 max-w-xs mx-auto sm:max-w-none"
        >
          <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          Chat with Anna
        </motion.a>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={4}
          className="mt-10 text-slate-400"
        >
          <ChevronDown className="w-5 h-5 animate-bounce mx-auto" />
        </motion.div>
      </section>

      {/* Features */}
      <section className="relative z-10 w-full max-w-md mx-auto px-4 sm:max-w-lg lg:max-w-xl pb-12">
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          custom={0}
          className="text-sm font-bold uppercase tracking-[0.2em] text-[#62D7D1] text-center mb-8"
        >
          How does it work?
        </motion.p>

        <div className="flex flex-col gap-6">
          {/* Card 1 — Check */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            custom={1}
            data-testid="feature-card-check"
            className="bg-white p-8 rounded-3xl shadow-[0_8px_40px_rgba(98,215,209,0.08)] border border-slate-100 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-gradient-to-br from-[#7ACFFF]/10 to-[#62D7D1]/10 -translate-y-1/2 translate-x-1/2" />
            <div className="w-12 h-12 rounded-2xl bg-[#62D7D1]/10 flex items-center justify-center mb-5">
              <Search className="w-6 h-6 text-[#62D7D1]" />
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-3">
              Check a client
            </h3>
            <p className="text-base text-slate-600 leading-relaxed">
              Send me a phone number. I'll instantly tell you if he's been reported by other girls.
            </p>
          </motion.div>

          {/* Card 2 — Report */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            custom={2}
            data-testid="feature-card-report"
            className="bg-white p-8 rounded-3xl shadow-[0_8px_40px_rgba(98,215,209,0.08)] border border-slate-100 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-gradient-to-br from-[#25D366]/10 to-[#62D7D1]/10 -translate-y-1/2 translate-x-1/2" />
            <div className="w-12 h-12 rounded-2xl bg-[#25D366]/10 flex items-center justify-center mb-5">
              <ShieldAlert className="w-6 h-6 text-[#25D366]" />
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-3">
              Report an incident
            </h3>
            <p className="text-base text-slate-600 leading-relaxed">
              Had a bad experience? Send me the number + what happened — I save everything and warn the others.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust banner */}
      <section className="relative z-10 w-full max-w-md mx-auto px-4 sm:max-w-lg lg:max-w-xl pb-16">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          custom={0}
          className="bg-slate-900 p-8 rounded-3xl text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#62D7D1]/10 to-transparent" />
          <div className="relative z-10">
            <Lock className="w-8 h-8 text-[#62D7D1] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-3">
              100% Confidential
            </h3>
            <p className="text-slate-400 leading-relaxed text-sm sm:text-base">
              Your data and searches are completely anonymous. An exclusive service for escorts in Switzerland.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 text-center pb-24 sm:pb-12 px-4">
        <p className="text-xs text-slate-400">
          BMG &middot; Confidential service &middot; Switzerland
        </p>
      </footer>

      {/* Floating WhatsApp FAB */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-fab"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white flex items-center justify-center shadow-[0_4px_20px_rgba(37,211,102,0.5)] transition-all duration-300 ${
          showFab ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </div>
  );
}
